/**
 * Configuration loading for the API handler
 * Supports multiple loading strategies with fallbacks
 */

import type { ApiConfig } from './types';

/**
 * Default configuration that works out of the box
 */
export function getDefaultApiConfig(): ApiConfig {
  return {
    scanPaths: ['./src', './app', './docs', './README.md', './.tickets'],
    include: ['**/*.md', '**/*.mdx', '**/*.tsx', '**/*.ts', '**/*.jsx', '**/*.js'],
    exclude: ['**/node_modules/**', '**/.next/**', '**/dist/**', '**/build/**'],
    features: {
      tasks: true,
      ai: false, // Disabled by default for safety
      realtime: true,
      files: true,
      health: true,
    },
    security: {
      authentication: false,
      rateLimit: false,
      cors: true, // Enabled for dev convenience
      allowedPaths: ['**/*'],
    },
    cache: {
      enabled: true,
      ttl: 300000, // 5 minutes
    },
  };
}

/**
 * Load configuration with multiple fallback strategies
 */
export async function loadApiConfig(): Promise<ApiConfig> {
  const defaultConfig = getDefaultApiConfig();

  // Strategy 1: Try project's route.ts config export
  try {
    // This would be overridden by user's route file if they export config
    const routeConfig = await tryLoadRouteConfig();
    if (routeConfig) {
      return mergeConfigs(defaultConfig, routeConfig);
    }
  } catch {
    // Continue to next strategy
  }

  // Strategy 2: Try vibeship.config.ts
  try {
    const configModule = await import(process.cwd() + '/vibeship.config');
    const userConfig = configModule.default || configModule;
    return mergeConfigs(defaultConfig, userConfig);
  } catch {
    // Continue to next strategy
  }

  // Strategy 3: Try package.json vibeship field
  try {
    const packageJson = await import(process.cwd() + '/package.json');
    if (packageJson.vibeship) {
      return mergeConfigs(defaultConfig, packageJson.vibeship);
    }
  } catch {
    // Continue to next strategy
  }

  // Strategy 4: Environment variables
  const envConfig = loadConfigFromEnv();
  if (envConfig) {
    return mergeConfigs(defaultConfig, envConfig);
  }

  // Final fallback: use defaults
  return defaultConfig;
}

/**
 * Try to load config from the user's route file
 */
async function tryLoadRouteConfig(): Promise<ApiConfig | null> {
  try {
    // This is a placeholder - in practice, the user's route file
    // could export a config object that we merge with defaults
    return null;
  } catch {
    return null;
  }
}

/**
 * Load configuration from environment variables
 */
function loadConfigFromEnv(): Partial<ApiConfig> | null {
  const envConfig: Partial<ApiConfig> = {};

  if (process.env.VIBESHIP_SCAN_PATHS) {
    envConfig.scanPaths = process.env.VIBESHIP_SCAN_PATHS.split(',').map(p => p.trim());
  }

  if (process.env.VIBESHIP_DISABLE_AI === 'true') {
    envConfig.features = { ai: false };
  }

  if (process.env.VIBESHIP_DISABLE_CACHE === 'true') {
    envConfig.cache = { enabled: false };
  }

  if (process.env.VIBESHIP_DISABLE_CORS === 'true') {
    envConfig.security = { cors: false };
  }

  return Object.keys(envConfig).length > 0 ? envConfig : null;
}

/**
 * Deep merge configuration objects
 */
function mergeConfigs(base: ApiConfig, override: Partial<ApiConfig>): ApiConfig {
  const result = { ...base };

  if (override.scanPaths) result.scanPaths = override.scanPaths;
  if (override.include) result.include = override.include;
  if (override.exclude) result.exclude = override.exclude;

  if (override.features) {
    result.features = { ...result.features, ...override.features };
  }

  if (override.security) {
    result.security = { ...result.security, ...override.security };
  }

  if (override.cache) {
    result.cache = { ...result.cache, ...override.cache };
  }

  return result;
}